import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { usePlayerProfile } from '@/hooks/usePlayerProfile';
import PlayerNewsList from './PlayerNewsList';
import {
  Newspaper,
  BarChart3,
  Clock,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface PlayerNewsDialogProps {
  playerName: string;
  /** players.id when the caller has it; PlayerNewsList prefers it over the name. */
  playerId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PlayerNewsDialog = ({ playerName, playerId, open, onOpenChange }: PlayerNewsDialogProps) => {
  // News is owned by PlayerNewsList now; the hook is still the source for the
  // performance tab's MLB season stats.
  const { stats, loading } = usePlayerProfile(playerName, open);



  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 overflow-hidden">
        <DialogHeader className="bg-gradient-to-r from-primary/20 via-primary/10 to-transparent p-6 pb-4">
          <DialogTitle className="text-2xl font-display font-bold">
            {playerName}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="news" className="flex-1">
          <TabsList className="w-full justify-start rounded-none border-b bg-transparent px-6 h-12">
            <TabsTrigger value="news" className="gap-2 data-[state=active]:bg-primary/10">
              <Newspaper className="w-4 h-4" />
              News
            </TabsTrigger>
            <TabsTrigger value="performance" className="gap-2 data-[state=active]:bg-primary/10">
              <BarChart3 className="w-4 h-4" />
              2026 Stats
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[400px]">
            {/* News Tab */}
            {/* SAB-1173: one list renderer for both sports. The dialog used to carry its
                own copy of this markup with its own news_type colours, which is how MLB
                and NFL would have drifted on what news looks like. */}
            <TabsContent value="news" className="p-6 pt-4 m-0">
              <PlayerNewsList sport="mlb" playerId={playerId} playerName={playerName} />
            </TabsContent>

            {/* Performance Tab */}
            <TabsContent value="performance" className="p-6 pt-4 m-0 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : !stats ? (
                <p className="text-center py-12 text-muted-foreground text-sm">
                  No performance data available for {playerName}
                </p>
              ) : stats.is_pitcher ? (
                /* Pitcher Stats */
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">2026 Pitching Stats</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-3">
                      {[
                        { label: 'G', value: stats.games_played },
                        { label: 'W', value: stats.pitcher_wins },
                        { label: 'L', value: stats.pitcher_losses },
                        { label: 'SV', value: stats.saves },
                        { label: 'IP', value: stats.innings_pitched.toFixed(1) },
                        { label: 'K', value: stats.pitcher_strikeouts },
                        { label: 'ER', value: stats.earned_runs },
                        { label: 'ERA', value: stats.innings_pitched > 0 ? (stats.earned_runs * 9 / stats.innings_pitched).toFixed(2) : '-.--' },
                        { label: 'HLD', value: stats.holds },
                        { label: 'BB', value: stats.walks_drawn },
                        { label: 'E', value: stats.errors },
                        { label: 'WHIP', value: stats.innings_pitched > 0 ? ((stats.walks_allowed + (stats.hits_allowed || 0)) / stats.innings_pitched).toFixed(2) : '-.--' },
                      ].map(({ label, value }) => (
                        <div key={label} className="text-center p-2 bg-muted/30 rounded-lg">
                          <p className="text-lg font-bold">{value}</p>
                          <p className="text-xs text-muted-foreground">{label}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                /* Hitter Stats */
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">2026 Batting Stats</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-3">
                      {[
                        { label: 'G', value: stats.games_played },
                        { label: 'AB', value: stats.at_bats },
                        { label: 'H', value: stats.hits },
                        { label: 'AVG', value: stats.at_bats > 0 ? (stats.hits / stats.at_bats).toFixed(3) : '.000' },
                        { label: 'HR', value: stats.home_runs },
                        { label: 'RBI', value: stats.rbi },
                        { label: 'R', value: stats.runs_scored },
                        { label: 'SB', value: stats.stolen_bases },
                        { label: 'BB', value: stats.walks_drawn },
                        { label: 'K', value: stats.strikeouts },
                        { label: 'E', value: stats.errors },
                        { label: 'OBP', value: stats.at_bats > 0 ? ((stats.hits + stats.walks_drawn) / (stats.at_bats + stats.walks_drawn)).toFixed(3) : '.000' },
                      ].map(({ label, value }) => (
                        <div key={label} className="text-center p-2 bg-muted/30 rounded-lg">
                          <p className="text-lg font-bold">{value}</p>
                          <p className="text-xs text-muted-foreground">{label}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default PlayerNewsDialog;
